/**
 * My Consumer Record — how NAVI (and connected institutions) understands and
 * supports the consumer. Not scores. Explanations.
 */
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { useWalletDerived } from "@/lib/wallet-journeys";
import { loadWallet } from "@/lib/wallet-storage";
import { useEffect, useState } from "react";
import { CheckCircle2, GraduationCap, MessageCircle, FileText, Sparkles, Loader2 } from "lucide-react";

export default function WalletRecord() {
  const { view, rows, error } = useWalletDerived();
  const [wallet, setWallet] = useState(null);
  useEffect(() => { setWallet(loadWallet()); }, []);

  const productsUnderstood = view ? Array.from(new Set(view.active.map(r => r.product_type).filter(Boolean))) : [];
  const currentlyLearning = view ? Array.from(new Set(view.upcoming.map(r => r.product_type).filter(Boolean))) : [];
  const journeysCompleted = rows ? rows.filter(r => r.status === "completed") : [];
  const language = (wallet?.language || "en").toUpperCase();
  const fontSize = wallet?.accessibility?.font_size || "medium";

  return (
    <WalletShell activeTab="record">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-8" data-testid="wallet-record">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Section</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>My Consumer Record</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            How NAVI understands and supports you. No scores. No opinions. Just what you&apos;ve learned, what you&apos;re still exploring, and how you like to be spoken to.
          </p>
        </header>

        {!view && !error && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {error && <div className="rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-800 p-4">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <RecordCard title="Products I understand" icon={CheckCircle2} tone="emerald" testid="record-understand">
            {productsUnderstood.length > 0
              ? <ChipRow items={productsUnderstood.map(p => p.replace(/_/g, " "))} />
              : <p className="text-sm text-slate-500 italic">Complete a review and NAVI marks it here.</p>
            }
          </RecordCard>

          <RecordCard title="Currently learning" icon={GraduationCap} tone="amber" testid="record-learning">
            {currentlyLearning.length > 0
              ? <ChipRow items={currentlyLearning.map(p => p.replace(/_/g, " "))} />
              : <p className="text-sm text-slate-500 italic">Nothing in progress.</p>
            }
          </RecordCard>

          <RecordCard title="Journeys completed" icon={Sparkles} tone="navy" testid="record-journeys">
            {journeysCompleted.length > 0
              ? <ul className="space-y-1.5">{journeysCompleted.slice(0, 6).map(j => <li key={j.journey_id} className="text-[13px] text-slate-700">· {j.product_name}</li>)}</ul>
              : <p className="text-sm text-slate-500 italic">Your completed reviews will appear here.</p>
            }
          </RecordCard>

          <RecordCard title="Support preferences" icon={MessageCircle} tone="gold" testid="record-preferences">
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <PrefRow label="Language" value={language} />
              <PrefRow label="Font size" value={fontSize.charAt(0).toUpperCase() + fontSize.slice(1)} />
              <PrefRow label="Prefers" value="Plain language" />
              <PrefRow label="Contact" value="Wallet only" />
            </div>
            <p className="text-[11px] text-slate-500 mt-3">You control these in Profile & settings. Nothing is shared with institutions without your consent.</p>
          </RecordCard>
        </div>
      </div>
    </WalletShell>
  );
}

function RecordCard({ title, icon: Icon, tone, testid, children }) {
  const t = {
    emerald: "#10B981",
    amber:   "#D97706",
    navy:    NAVY,
    gold:    GOLD,
  }[tone];
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${t}18` }}>
          <Icon className="w-4 h-4" style={{ color: t }} />
        </div>
        <div className="text-sm font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function ChipRow({ items }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => <span key={i} className="text-[11px] rounded-full bg-slate-100 text-slate-700 px-2.5 py-1">{it}</span>)}
    </div>
  );
}

function PrefRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-[13px] mt-0.5" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
